import type { ILocation } from '../../../../../models/location';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { TransportSubMode } from '../../../../../models/transport-sub-mode.enum';
import { InterchangeDuration } from './interchange-duration.enum';

export interface ITravelPlannerFilters {
  onlyDirectConnections: boolean;
  viaStopArea: ILocation | undefined;
  interchangeDuration: InterchangeDuration;
  includeNearbyStopAreas: boolean;
  showWalking: boolean;
  showBiking: boolean;
  bikingMaxDistance: number;
  bikingSpeed: number;
  transportModes: TransportMode[];
  transportSubModes: TransportSubMode[];
  showOccupancy: boolean;
  transportModeToDeparture: TransportMode;
}

export const initialTravelPlannerFilters: ITravelPlannerFilters = {
  onlyDirectConnections: false,
  viaStopArea: undefined,
  interchangeDuration: InterchangeDuration.normal,
  includeNearbyStopAreas: false,
  showWalking: false,
  showBiking: false,
  bikingMaxDistance: 10000,
  bikingSpeed: 85,
  transportModes: [TransportMode.tram, TransportMode.bus, TransportMode.ferry, TransportMode.train],
  transportSubModes: [TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain],
  showOccupancy: false,
  transportModeToDeparture: TransportMode.walk,
};

// NV-5212 - Add buss on demand transport mode if feature flag is enabled
export const initialTravelPlannerFiltersWithBussOnDemand: ITravelPlannerFilters = {
  onlyDirectConnections: false,
  viaStopArea: undefined,
  interchangeDuration: InterchangeDuration.normal,
  includeNearbyStopAreas: false,
  showWalking: false,
  showBiking: false,
  bikingMaxDistance: 10000,
  bikingSpeed: 85,
  transportModes: [TransportMode.tram, TransportMode.bus, TransportMode.ferry, TransportMode.train, TransportMode.bussondemand],
  transportSubModes: [TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain],
  showOccupancy: false,
  transportModeToDeparture: TransportMode.walk,
};

export const filtersAreEqual = (a: ITravelPlannerFilters, b: ITravelPlannerFilters): boolean => JSON.stringify(a) === JSON.stringify(b);

export const isValidTravelPlannerFilters = (obj: unknown) => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const obj2 = obj as ITravelPlannerFilters;
  if (obj2.onlyDirectConnections !== undefined && typeof obj2.onlyDirectConnections !== 'boolean') {
    return false;
  }
  if (obj2.viaStopArea !== undefined && typeof obj2.viaStopArea !== 'object') {
    return false;
  }
  if (obj2.interchangeDuration !== undefined && !Object.values(InterchangeDuration).includes(obj2.interchangeDuration)) {
    return false;
  }
  if (obj2.includeNearbyStopAreas !== undefined && typeof obj2.includeNearbyStopAreas !== 'boolean') {
    return false;
  }
  if (obj2.showWalking !== undefined && typeof obj2.showWalking !== 'boolean') {
    return false;
  }
  if (obj2.showBiking !== undefined && typeof obj2.showBiking !== 'boolean') {
    return false;
  }
  if (obj2.bikingMaxDistance !== undefined && typeof obj2.bikingMaxDistance !== 'number') {
    return false;
  }
  if (obj2.bikingSpeed !== undefined && typeof obj2.bikingSpeed !== 'number') {
    return false;
  }
  if (obj2.transportModes !== undefined && (!Array.isArray(obj2.transportModes) || obj2.transportModes.some((mode: TransportMode) => !Object.values(TransportMode).includes(mode)))) {
    return false;
  }
  if (
    obj2.transportSubModes !== undefined &&
    (!Array.isArray(obj2.transportSubModes) || obj2.transportSubModes.some((subMode: TransportSubMode) => !Object.values(TransportSubMode).includes(subMode)))
  ) {
    return false;
  }
  if (obj2.showOccupancy !== undefined && typeof obj2.showOccupancy !== 'boolean') {
    return false;
  }
  if (obj2.transportModeToDeparture !== undefined && !Object.values(TransportMode).includes(obj2.transportModeToDeparture)) {
    return false;
  }

  return true;
};
